import { useContext, useEffect, useRef, useState } from "react";
import { MultiSelect } from "react-multi-select-component";
import { useServerPagination } from "../../../../hooks/customHook/paginationHook/useServerPagination";
import { useNavigate } from "react-router-dom";
import BASE_URL from "../../../../assets/global/url";
import { PaginationControls } from "../../../../hooks/customHook/paginationHook/usePagination";
import DataTable from "react-data-table-component";
import { customStyles } from "../../../../assets/global/table-style";
import { RefreshContext } from "../../Payable";

import FilterStore from "../../../../hooks/customHook/FilterStore";
import { useServerSort } from "../../../../hooks/customHook/sortingHook/useTableSort";

const ApprovedStatusTab = ({
  selectedCutOff,
  setSelectedCutOff, // used to expand/restore date range during search
  currencyId,
  vendorDataFilter,
  setSearchTermFilter,
  columns,
  clearFilterSignal, // incremented by parent's handleClearFilter
}) => {
  const refreshKey = useContext(RefreshContext);
  const navigate = useNavigate();
  const debounceTimer = useRef();
  const pageRestored = useRef(false);

  // --- Filter retention ---
  const [searchText, setSearchText] = useState(() =>
    FilterStore.PMgetSearch(`payable`),
  );
  useEffect(() => {
    FilterStore.PMsaveSearch(searchText, `payable`);
  }, [searchText]);

  const [filterColumn, setFilterColumn] = useState(() =>
    FilterStore.PMgetFilterColumn(`payable_approved`),
  );
  useEffect(() => {
    FilterStore.PMsaveFilterColumn(filterColumn, `payable_approved`);
  }, [filterColumn]);

  const [selectedVendor, setSelectedVendor] = useState(() =>
    FilterStore.getSelected(),
  );
  useEffect(() => {
    FilterStore.saveSelected(selectedVendor);
  }, [selectedVendor]);

  /**
   * Stores the cutoff that was active BEFORE the user started typing in the
   * search box. Null means we are not currently in "search mode".
   */
  const priorCutOffRef = useRef(null);

  /**
   * Always holds the latest selectedCutOff value so the debounced callback
   * (which closes over a stale prop) can still read the current range.
   */
  const selectedCutOffRef = useRef(selectedCutOff);
  useEffect(() => {
    selectedCutOffRef.current = selectedCutOff;
  }, [selectedCutOff]);

  const approvedPayablePagination = useServerPagination(
    `${BASE_URL}/payable/fetchPayableSs`,
    FilterStore.PMgetPerPage(`payable_approved`),
  );

  const sort = useServerSort(approvedPayablePagination);

  // --- Page / PerPage retention ---
  // Save page — gated behind pageRestored so the hook's internal reset-to-1
  // from updateParams on mount doesn't clobber the stored page.
  useEffect(() => {
    if (!pageRestored.current) return;
    if (approvedPayablePagination.currentPage) {
      FilterStore.PMsavePage(
        approvedPayablePagination.currentPage,
        `payable_approved`,
      );
    }
  }, [approvedPayablePagination.currentPage]);

  useEffect(() => {
    if (approvedPayablePagination.itemsPerPage) {
      FilterStore.PMsavePerPage(
        approvedPayablePagination.itemsPerPage,
        `payable_approved`,
      );
    }
  }, [approvedPayablePagination.itemsPerPage]);

  // Restore last viewed page on mount. setTimeout(600) defers until after the
  // mount-time fetchPayable → updateParams → hook reset-to-1 has settled,
  // then opens the save gate for user navigation.
  useEffect(() => {
    const savedPage = FilterStore.PMgetPage(`payable_approved`);
    if (savedPage > 1) {
      setTimeout(() => {
        approvedPayablePagination.setCurrentPage(savedPage);
        pageRestored.current = true;
      }, 600);
    } else {
      setTimeout(() => {
        pageRestored.current = true;
      }, 600);
    }
  }, []);

  // --- Point 2: Dynamically update the displayed date range to match results ---
  // Runs whenever search results change. Only active while in search mode
  // (priorCutOffRef.current !== null). Computes the min/max purchaseDate across
  // the current page of results and pushes that range into the DateRangePicker
  // in the parent so the user can see the span their search covers.
  useEffect(() => {
    // Guard: only update range while in search mode
    if (priorCutOffRef.current === null) return;

    const data = approvedPayablePagination.data;
    if (!data || data.length === 0) return;

    const dates = data
      .map((item) => new Date(item.purchaseDate))
      .filter((d) => !isNaN(d.getTime()));
    if (dates.length === 0) return;

    const minDate = new Date(Math.min(...dates));
    const maxDate = new Date(Math.max(...dates));

    // Only call setter when the range actually changed to avoid extra renders
    const currentFrom = selectedCutOffRef.current?.from;
    const currentTo = selectedCutOffRef.current?.to;
    const sameFrom =
      currentFrom instanceof Date &&
      currentFrom.getTime() === minDate.getTime();
    const sameTo =
      currentTo instanceof Date && currentTo.getTime() === maxDate.getTime();
    if (sameFrom && sameTo) return;

    setSelectedCutOff({ id: "search-range", from: minDate, to: maxDate });
  }, [approvedPayablePagination.data]);

  // --- Vendor filter ---
  const handleVendorChange = (selected) => {
    setSelectedVendor(selected);

    approvedPayablePagination.updateApiUrl(
      `${BASE_URL}/payable/fetchPayableSs`,
    );
    approvedPayablePagination.updateParams({
      startDate: selectedCutOff?.from,
      endDate: selectedCutOff?.to,
      currencyId,
      selectedVendor: selected,
      status: "Approved",
    });
  };

  // --- Search ---
  const handleSearch = (value) => {
    const raw = String(value).replace(/,/g, "");

    // Format with thousands separators for display
    const [intPart, decimalPart] = raw.split(".");
    const withCommas = intPart.replace(/\B(?=(\d{3})+(?!\d))/g, ",");
    const formatted =
      decimalPart !== undefined ? `${withCommas}.${decimalPart}` : withCommas;

    const isMatchingTotalAmount = approvedPayablePagination.data.some((item) =>
      String(item.totalPrice).includes(raw),
    );

    let searchValue;
    if (isMatchingTotalAmount) {
      setSearchText(formatted);
      searchValue = formatted;
    } else {
      setSearchText(raw);
      searchValue = raw;
    }

    // ── Date-range management ────────────────────────────────────────────────
    if (raw.trim() !== "" && priorCutOffRef.current === null) {
      // Search is STARTING — save current cutoff and open the range to "all".
      // The backend treats null startDate/endDate as "no date restriction".
      priorCutOffRef.current = selectedCutOff;
      setSelectedCutOff({ id: "search-all", from: null, to: null });
    } else if (raw.trim() === "" && priorCutOffRef.current !== null) {
      // Search is CLEARED — restore the cutoff that was active before searching.
      // The useEffect below will re-fetch with the restored range automatically.
      setSelectedCutOff(priorCutOffRef.current);
      priorCutOffRef.current = null;
      return; // skip debounced search call; the effect handles the re-fetch
    }
    // ────────────────────────────────────────────────────────────────────────

    clearTimeout(debounceTimer.current);
    debounceTimer.current = setTimeout(() => {
      // Use selectedCutOffRef so we always have the latest value even though
      // this callback closes over a potentially stale prop.
      const cutoff = selectedCutOffRef.current;

      approvedPayablePagination.updateApiUrl(
        `${BASE_URL}/payable/fetchPayableSearch`,
      );
      approvedPayablePagination.updateParams({
        // Always pass null dates during search so results are not date-restricted.
        // The date range display (Point 2) is updated separately from results.
        startDate: null,
        endDate: null,
        filterColumn: filterColumn,
        searchText: searchValue,
        currencyId,
        status: "Approved",
      });
    }, 500);
  };

  // --- Clear Filter signal from parent ---
  // When the parent fires handleClearFilter it increments clearFilterSignal.
  // Reset search text, vendor selection, filter column, and the prior-cutoff
  // ref so the tab is back to a clean state matching the "show all" cutoff.
  useEffect(() => {
    if (clearFilterSignal === 0) return; // skip the initial render
    setSearchText("");
    FilterStore.PMsaveSearch("", "payable");
    setSelectedVendor([]);
    FilterStore.saveSelected([]);
    setFilterColumn("all");
    FilterStore.PMsaveFilterColumn("all", "payable_approved");
    priorCutOffRef.current = null; // exit any in-progress search mode
  }, [clearFilterSignal]);

  // --- Main data fetch (triggered by filter/cutoff/currency/refresh changes) ---
  const fetchPayable = () => {
    // Point 2 loop guard: when the user is actively searching, always pass null
    // dates so that a selectedCutOff update triggered by the results-range
    // guard the cycle would be: results → update cutoff → re-fetch with dates
    //  results → new dates → re-fetch … (infinite loop).
    const isSearching = searchText && searchText.trim() !== "";

    let apiUrl = `${BASE_URL}/payable/fetchPayableSs`;

    if (isSearching) {
      apiUrl = `${BASE_URL}/payable/fetchPayableSearch`;
    } else if (selectedVendor && selectedVendor.length > 0) {
      apiUrl = `${BASE_URL}/payable/fetchPayableSs`;
    }

    approvedPayablePagination.updateApiUrl(apiUrl);
    approvedPayablePagination.updateParams({
      startDate: isSearching ? null : (selectedCutOff?.from ?? null),
      endDate: isSearching ? null : (selectedCutOff?.to ?? null),
      currencyId,
      filterColumn: filterColumn,
      searchText: searchText,
      selectedVendor,
      status: "Approved",
    });
  };

  useEffect(() => {
    fetchPayable();
  }, [selectedVendor, currencyId, selectedCutOff, refreshKey]);

  return (
    <div className="container-fluid">
      <div className="row">
        <div className="col-sm-12 col-md-4">
          <span>Suppliers</span>
          <MultiSelect
            options={vendorDataFilter} // You can also use vendorFetch here if needed
            value={selectedVendor}
            onChange={handleVendorChange}
            labelledBy="Select"
            className="w-100"
            style={{ maxWidth: "1000px" }}
            filterOptions={(options, filter) => {
              setSearchTermFilter(filter); // always update, even when ""
              return options.filter(({ label }) =>
                label.toLowerCase().includes(filter.toLowerCase()),
              );
            }}
          />
        </div>
      </div>

      <div className="input-group my-3">
        <input
          type="text"
          className="form-control"
          placeholder="Search"
          value={searchText}
          onChange={(e) => handleSearch(e.target.value)}
        />

        <button
          type="button"
          className="btn btn-outline-secondary dropdown-toggle-split"
          data-bs-toggle="dropdown"
          aria-expanded="false"
        >
          <i className="fa-solid fa-sliders"></i>
        </button>
        <ul className="dropdown-menu dropdown-menu-end">
          <li>
            <button
              className={`dropdown-item ${filterColumn === "all" ? "active" : ""}`}
              onClick={() => setFilterColumn("all")}
            >
              All
            </button>
          </li>
          <li>
            <button
              className={`dropdown-item ${filterColumn === "product" ? "active" : ""}`}
              onClick={() => setFilterColumn("product")}
            >
              Product Name/Code
            </button>
          </li>
          <li>
            <button
              className={`dropdown-item ${filterColumn === "transaction_date" ? "active" : ""}`}
              onClick={() => setFilterColumn("transaction_date")}
            >
              Transaction Date
            </button>
          </li>
          <li>
            <button
              className={`dropdown-item ${filterColumn === "transaction_id" ? "active" : ""}`}
              onClick={() => setFilterColumn("transaction_id")}
            >
              Transaction No.
            </button>
          </li>
          <li>
            <button
              className={`dropdown-item ${filterColumn === "warehouse.name" ? "active" : ""}`}
              onClick={() => setFilterColumn("warehouse.name")}
            >
              Receiving Warehouse
            </button>
          </li>
          <li>
            <button
              className={`dropdown-item ${filterColumn === "MOP" ? "active" : ""}`}
              onClick={() => setFilterColumn("MOP")}
            >
              Mode of Payment
            </button>
          </li>
          <li>
            <button
              className={`dropdown-item ${filterColumn === "vendor.company_name" ? "active" : ""}`}
              onClick={() => setFilterColumn("vendor.company_name")}
            >
              Supplier
            </button>
          </li>
          <li>
            <button
              className={`dropdown-item ${filterColumn === "discount_value" ? "active" : ""}`}
              onClick={() => setFilterColumn("discount_value")}
            >
              Discount
            </button>
          </li>
          <li>
            <button
              className={`dropdown-item ${filterColumn === "due_date" ? "active" : ""}`}
              onClick={() => setFilterColumn("due_date")}
            >
              Deadline
            </button>
          </li>
          <li>
            <button
              className={`dropdown-item ${filterColumn === "purchaseDate" ? "active" : ""}`}
              onClick={() => setFilterColumn("purchaseDate")}
            >
              Purchase Date
            </button>
          </li>
          <li>
            <button
              className={`dropdown-item ${filterColumn === "totalPrice" ? "active" : ""}`}
              onClick={() => setFilterColumn("totalPrice")}
            >
              Total Price
            </button>
          </li>
        </ul>
      </div>

      <div className="data-table-cell-width">
        <DataTable
          columns={sort.sortColumns(columns)}
          data={approvedPayablePagination.data}
          customStyles={customStyles}
          highlightOnHover
          className="dataTable"
          onRowClicked={(row) => {
            if (row.client_transaction_id?.startsWith("ROT-PO-")) {
              navigate(`../purchases/re-open-tickets/${row.id}?page=${1}`, {
                state: { isViewMode: true },
              });
            } else {
              navigate(`../purchases/view_payable/${row.id}?page=${1}`);
            }
          }}
          {...sort.sortProps}
        />
        <PaginationControls {...approvedPayablePagination} />
      </div>
    </div>
  );
};

export default ApprovedStatusTab;
